import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SelectedApp } from "@/components/applications/ApplicationCatalog";
import { Button } from "@/components/ui/button";
import { useApplications } from "@/hooks/useApplications";
import { useAppConfigurations } from "@/hooks/useAppConfigurations";
import { Application, AppPlatform } from "@/types/application";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, Save, Send, Loader2, Activity, CheckCircle2, XCircle, MinusCircle, X } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ApplicationWizardSteps, ApplicationWizardStep } from "@/components/applications/ApplicationWizardSteps";
import { SelectAppStep } from "@/components/applications/steps/SelectAppStep";
import { AppAssignmentStep } from "@/components/applications/steps/AppAssignmentStep";
import { AppConfigurationStep, InitialConfiguration } from "@/components/applications/steps/AppConfigurationStep";
import { PppcStep } from "@/components/applications/steps/PppcStep";
import { fetchAppByTrackId, savePublicApp } from "@/services/appStoreApi";
import { assignAppTags } from "@/services/tagAssignmentApi";
import { getCurrentUserEmail } from "@/lib/auth";
import { useCompactMode } from "@/hooks/useCompactMode";
import {
  PrivacyPermissionsFormValue,
  emptyPppcFormValue,
  validatePppcForm,
} from "@/components/applications/pppc/PrivacyPermissionsForm";
import { useSavePppcPolicy, usePppcPolicy, usePppcPolicyTags } from "@/hooks/usePppcPolicy";
import { recordPppcActivity } from "@/services/pppcActivityLog";
import { logPlatformGating } from "@/utils/platformGatingLog";

function detectPlatform(selected: SelectedApp | null): AppPlatform | null {
  if (!selected) return null;
  if (selected.type === "appStore") return selected.app.platform;
  if (selected.type === "abm") return selected.app.platform;
  // private (.pkg) — infer from app.platform string
  const raw = (selected as any).app?.platform ?? "";
  return String(raw).toLowerCase().includes("ios") ? "ios" : "macos";
}

function detectBundleId(selected: SelectedApp | null): string {
  if (!selected) return "";
  if (selected.type === "appStore") return selected.app.bundleId ?? "";
  if (selected.type === "abm") return selected.app.bundleId ?? "";
  return (selected as any).app?.bundleId ?? "";
}

function detectAppName(selected: SelectedApp | null): string {
  if (!selected) return "";
  return (selected as any).app?.name ?? "";
}

export default function AddApplicationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefillSearch = (location.state as { prefillSearch?: string } | null)?.prefillSearch ?? "";
  const remediationContext = (location.state as { remediationContext?: { appName: string; bundleId: string; cveCount: number; vulnerableVersions: string[] } } | null)?.remediationContext;
  const { cx } = useCompactMode();
  const { addApplication } = useApplications();
  const { createConfiguration } = useAppConfigurations();
  const [currentStep, setCurrentStep] = useState<ApplicationWizardStep>("select");
  const [completedSteps, setCompletedSteps] = useState<ApplicationWizardStep[]>([]);
  const [selectedApp, setSelectedApp] = useState<SelectedApp | null>(null);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [abmCustomer, setAbmCustomer] = useState<{ id: string; name: string } | null>(null);
  const [initialConfiguration, setInitialConfiguration] = useState<InitialConfiguration | null>(null);
  const [pppcValue, setPppcValue] = useState<PrivacyPermissionsFormValue>(emptyPppcFormValue());
  const [pppcErrors, setPppcErrors] = useState<ReturnType<typeof validatePppcForm>>({});
  // Tracks an in-flight submission so we can disable both menu items, show
  // a spinner on the trigger, and reject re-entrant clicks while the multi-
  // step publish flow (create → tag-assign → PPPC) is running.
  const [submitMode, setSubmitMode] = useState<"draft" | "publish" | null>(null);
  const isSubmitting = submitMode !== null;
  // When the user attempts to leave during submission, we stash the deferred
  // navigation here and surface a confirmation dialog. Confirm = run it,
  // cancel = drop it and stay on the wizard.
  const [pendingNav, setPendingNav] = useState<null | (() => void)>(null);
  // Latest value of isSubmitting for use inside non-React event listeners
  // (popstate/beforeunload) without re-binding them on every state tick.
  const isSubmittingRef = useRef(isSubmitting);
  useEffect(() => { isSubmittingRef.current = isSubmitting; }, [isSubmitting]);

  // Lightweight per-attempt timeline: records the start time and each step
  // outcome (success / skipped / error code) so the user can see what happened
  // without opening devtools. Only the most recent attempt is displayed.
  type TimelineStatus = "running" | "success" | "skipped" | "error";
  type TimelineEvent = {
    label: string;
    status: TimelineStatus;
    at: number;        // ms since attempt start
    errorCode?: string;
  };
  type Attempt = {
    id: string;
    mode: "draft" | "publish";
    startedAt: number; // epoch ms
    finishedAt: number | null;
    outcome: "success" | "error" | "running";
    events: TimelineEvent[];
  };
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const attemptRef = useRef<Attempt | null>(null);
  const [showTimeline, setShowTimeline] = useState(false);

  const startAttempt = (mode: "draft" | "publish"): Attempt => {
    const a: Attempt = {
      id: Math.random().toString(36).slice(2, 8),
      mode,
      startedAt: Date.now(),
      finishedAt: null,
      outcome: "running",
      events: [],
    };
    attemptRef.current = a;
    setAttempt(a);
    setShowTimeline(true);
    return a;
  };

  const recordStep = (
    label: string,
    status: TimelineStatus,
    errorCode?: string,
  ) => {
    const a = attemptRef.current;
    if (!a) return;
    const event: TimelineEvent = {
      label,
      status,
      at: Date.now() - a.startedAt,
      errorCode,
    };
    a.events = [...a.events, event];
    setAttempt({ ...a });
  };

  const finishAttempt = (outcome: "success" | "error") => {
    const a = attemptRef.current;
    if (!a) return;
    a.outcome = outcome;
    a.finishedAt = Date.now();
    setAttempt({ ...a });
    attemptRef.current = null;
  };

  /** Best-effort error code extraction for display in the timeline. */
  const errorCodeOf = (err: unknown): string => {
    if (!err) return "unknown";
    const anyErr = err as { code?: string | number; status?: number; name?: string; message?: string };
    if (anyErr.code != null) return String(anyErr.code);
    if (anyErr.status != null) return `HTTP ${anyErr.status}`;
    if (anyErr.name) return anyErr.name;
    if (anyErr.message) return anyErr.message.slice(0, 60);
    return "unknown";
  };

  const platform = detectPlatform(selectedApp);
  const bundleId = detectBundleId(selectedApp);
  const appName = detectAppName(selectedApp);
  const isMacOs = platform === "macos";

  // Reuse the same save+reconcile+invalidate+toast pipeline that the
  // standalone PPPC wizard uses, so both flows stay behaviorally aligned.
  // Toasting / cache invalidation / tag reconciliation all live in the hook.
  const savePppcMutation = useSavePppcPolicy(bundleId);

  // Look up any pre-existing PPPC policy bound to this bundle. We need:
  //   - existingPolicyId  → so save updates instead of duplicating
  //   - previousTagIds    → so reconcilePolicyTags computes correct add/remove
  //                          deltas instead of treating every save as a fresh
  //                          deploy (which silently breaks tag removals).
  const { data: existingPppcPolicy } = usePppcPolicy(bundleId);
  const existingPppcPolicyId = existingPppcPolicy?.policyId;
  const { data: existingPppcPolicyTags } = usePppcPolicyTags(existingPppcPolicyId);
  const existingPppcTagIds = useMemo(
    () => (existingPppcPolicyTags ?? []).map((t) => t.id),
    [existingPppcPolicyTags],
  );

  const steps = useMemo<ApplicationWizardStep[]>(
    () =>
      isMacOs
        ? ["select", "assignment", "configuration", "pppc"]
        : ["select", "assignment", "configuration"],
    [isMacOs],
  );
  const isAbmApp = selectedApp?.type === 'abm';
  const isLastStep = currentStep === steps[steps.length - 1];

  useEffect(() => {
    if (selectedApp?.type !== 'abm') { setAbmCustomer(null); }
  }, [selectedApp]);

  // If user moves off macOS after starting, drop them out of pppc step
  useEffect(() => {
    if (!isMacOs && currentStep === "pppc") {
      logPlatformGating({
        feature: "pppc",
        surface: "AddApplicationPage:PppcStep",
        reason: "platform-changed-mid-wizard",
        platform: platform ?? null,
        bundleId: bundleId ?? null,
        extra: { appName, redirectedTo: "configuration" },
      });
      setCurrentStep("configuration");
    }
  }, [isMacOs, currentStep, platform, bundleId, appName]);

  const handleSelectApp = (selection: SelectedApp) => {
    setSelectedApp(selection);
    if (selection.type !== 'abm') { setSelectedCustomers([]); }
    setSelectedGroups([]); setSelectedTags([]);
    setPppcValue(emptyPppcFormValue());
    setPppcErrors({});
  };

  const getSelectedAppId = (): string | undefined => {
    if (!selectedApp) return undefined;
    return selectedApp.type === 'appStore' ? selectedApp.app.appStoreId : selectedApp.app.id;
  };

  const canProceedFromStep = (step: ApplicationWizardStep): boolean => {
    switch (step) {
      case "select": return selectedApp !== null;
      case "assignment": return selectedCustomers.length > 0;
      case "configuration": return true;
      case "pppc": return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (!canProceedFromStep(currentStep)) {
      if (currentStep === "select") toast.error("Please select an application");
      return;
    }
    if (!completedSteps.includes(currentStep)) setCompletedSteps(prev => [...prev, currentStep]);
    const ci = steps.indexOf(currentStep);
    if (ci < steps.length - 1) setCurrentStep(steps[ci + 1]);
  };

  /**
   * Wraps any in-app navigation that should be blocked while a submit flow
   * is in flight. If submitting, it stashes the action and opens a confirm
   * dialog; otherwise the action runs immediately.
   */
  const guardedNavigate = useCallback((action: () => void) => {
    if (isSubmittingRef.current) {
      setPendingNav(() => action);
      return;
    }
    action();
  }, []);

  const handleBack = () => {
    guardedNavigate(() => {
      const ci = steps.indexOf(currentStep);
      if (ci > 0) setCurrentStep(steps[ci - 1]);
      else navigate('/applications');
    });
  };

  // Browser-level guards: warn on tab close/reload, and intercept the back/
  // forward button by re-pushing our entry and surfacing the same dialog.
  useEffect(() => {
    if (!isSubmitting) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore custom strings but require returnValue to be set.
      e.returnValue = "";
      return "";
    };

    // Push a sentinel entry so the first popstate keeps us on this URL.
    const sentinelUrl = window.location.href;
    window.history.pushState({ __addAppGuard: true }, "", sentinelUrl);

    const onPopState = () => {
      // Re-pin the URL and ask the user — confirming will pop again to leave.
      window.history.pushState({ __addAppGuard: true }, "", sentinelUrl);
      setPendingNav(() => () => { window.history.go(-2); });
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("popstate", onPopState);
    };
  }, [isSubmitting]);

  const handleSkipPppc = () => {
    setPppcValue(emptyPppcFormValue());
    setPppcErrors({});
  };

  const hasPppcChoices = Object.values(pppcValue.choices).some(
    (c) => c === "allow" || c === "deny",
  );

  /**
   * Scroll the first validation error into view and focus it. Used when a
   * submit attempt is blocked by a missing/invalid field — combined with the
   * persistent disabled state on the submit buttons, this guarantees the user
   * sees what's wrong instead of clicking again on a silently-disabled menu.
   */
  const focusFirstError = (target: ApplicationWizardStep) => {
    // Defer until after the step transition has rendered the anchor.
    requestAnimationFrame(() => {
      const root = document.querySelector<HTMLElement>(
        `[data-error-anchor="${target}"]`,
      );
      if (!root) return;
      // Prefer an explicitly-marked invalid field, fall back to the step root.
      const invalid =
        root.querySelector<HTMLElement>('[data-invalid="true"]') ??
        root.querySelector<HTMLElement>('[aria-invalid="true"]') ??
        root;
      invalid.scrollIntoView({ behavior: "smooth", block: "center" });
      // Only focusables can receive focus; tabindex=-1 lets the root accept it.
      if (!invalid.hasAttribute("tabindex")) invalid.setAttribute("tabindex", "-1");
      try { invalid.focus({ preventScroll: true }); } catch { /* noop */ }
    });
  };

  const handleAddApplication = async (publish: boolean = false) => {
    // Reject re-entrant clicks: a previous submission is still running.
    if (isSubmitting) {
      console.info('[AddApplicationPage] ignoring click — submission already in progress', { publish, currentMode: submitMode });
      return;
    }
    console.info('[AddApplicationPage] handleAddApplication clicked', {
      publish,
      hasApp: !!selectedApp,
      customerCount: selectedCustomers.length,
      tagCount: selectedTags.length,
    });
    if (!selectedApp) {
      toast.error("Please select an application");
      if (currentStep !== "select") setCurrentStep("select");
      focusFirstError("select");
      return;
    }
    if (selectedCustomers.length === 0) {
      toast.error("Please assign at least one customer");
      if (currentStep !== "assignment") setCurrentStep("assignment");
      focusFirstError("assignment");
      return;
    }

    // Pre-validate PPPC if the user supplied any choices
    if (isMacOs && hasPppcChoices) {
      const v = validatePppcForm(pppcValue);
      setPppcErrors(v);
      if (v.codeRequirement) {
        toast.error("PPPC: Code Requirement is required");
        if (currentStep !== "pppc") setCurrentStep("pppc");
        focusFirstError("pppc");
        return;
      }
      if (v.serviceIssues && v.serviceIssues.length > 0) {
        toast.error(
          v.serviceIssues.length === 1
            ? `PPPC: ${v.serviceIssues[0].message}`
            : `PPPC: ${v.serviceIssues.length} invalid custom TCC services`,
        );
        if (currentStep !== "pppc") setCurrentStep("pppc");
        focusFirstError("pppc");
        return;
      }
    }

    setSubmitMode(publish ? "publish" : "draft");
    startAttempt(publish ? "publish" : "draft");
    try {
      let createdAppId: string | null = null;

      if (selectedApp.type === 'appStore') {
        const app = selectedApp.app;
        // Fetch full app details from the backend before saving
        let fullAppData: any = null;
        if (app.appStoreId) {
          try {
            fullAppData = await fetchAppByTrackId(app.appStoreId, 'US');
            recordStep("Fetch App Store metadata", "success");
          } catch (e) {
            console.warn('Could not fetch full app details, proceeding with search data', e);
            recordStep("Fetch App Store metadata", "skipped", errorCodeOf(e));
          }
        } else {
          recordStep("Fetch App Store metadata", "skipped", "no trackId");
        }

        // Build payload matching the backend AppStoreApplication schema
        const payload = fullAppData || {
          id: null,
          name: app.name,
          bundleId: app.bundleId,
          applicationType: "PUBLIC",
          platform: app.platform === 'macos' ? 'macOS' : 'iOS',
          version: app.version,
          size: app.size,
          description: app.description,
          trackId: app.appStoreId,
          trackViewUrl: app.appStoreUrl,
          iconUrl: app.icon,
          minimumOsVersion: app.minimumOsVersion,
        };

        const saved = await savePublicApp(payload);
        createdAppId = saved.id ?? null;
        recordStep("Save application", "success");
        toast.success(publish ? "Application added" : "Application saved as draft");

        if (initialConfiguration && saved.id) {
          try {
            await createConfiguration({
              applicationId: saved.id,
              name: initialConfiguration.name,
              description: initialConfiguration.description,
              configurationData: { keys: initialConfiguration.keys },
              customers: selectedCustomers,
              isActive: true,
            });
            recordStep("Save app configuration", "success");
          } catch (e) {
            recordStep("Save app configuration", "error", errorCodeOf(e));
            throw e;
          }
        }
      } else if (selectedApp.type === 'abm') {
        const app = selectedApp.app;
        const newApp: Omit<Application, "id"> = {
          name: app.name, icon: app.icon, version: app.version,
          size: 'Unknown', releaseDate: new Date().toISOString().split('T')[0],
          source: 'abm', platform: app.platform, bundleId: app.bundleId,
          customers: selectedCustomers, status: publish ? 'published' : 'draft',
          abmConnectionId: app.abmConnectionId, licenseTotal: app.licenseTotal,
          licenseUsed: app.licenseUsed,
        };
        const created = await addApplication(newApp);
        createdAppId = created?.id ?? null;
        recordStep("Save ABM application", "success");
        toast.success(publish ? "Application added" : "Application saved as draft");
      } else {
        const app = selectedApp.app;
        const platformVal: AppPlatform =
          (app.platform ?? "").toLowerCase().includes("ios") ? "ios" : "macos";
        const newApp: Omit<Application, "id"> = {
          name: app.name,
          icon: "",
          version: app.version ?? "1.0",
          size: app.size ?? "Unknown",
          releaseDate: new Date().toISOString().split('T')[0],
          source: 'private',
          platform: platformVal,
          bundleId: app.bundleId,
          customers: selectedCustomers,
          status: publish ? 'published' : 'draft',
        };
        const created = await addApplication(newApp);
        createdAppId = created?.id ?? null;
        recordStep("Save private application", "success");
        toast.success(
          publish ? "Private application added" : "Private application saved as draft"
        );
      }

      // Publish-time tag assignment: assigning tags triggers backend deployment.
      // Save-as-draft skips this so the app stays unscoped until later.
      if (publish && createdAppId && selectedTags.length > 0) {
        try {
          const actor = getCurrentUserEmail() ?? "";
          await assignAppTags({
            app_ids: [createdAppId],
            tag_ids: selectedTags,
            created_by: actor,
            updated_by: actor,
          });
          recordStep("Assign tags (deploy)", "success");
          toast.success("Deployment started — tags assigned");
        } catch (e) {
          console.error("Failed to assign tags for deployment:", e);
          recordStep("Assign tags (deploy)", "error", errorCodeOf(e));
          toast.error("App created, but tag assignment (deployment) failed");
        }
      } else if (publish && selectedTags.length === 0) {
        recordStep("Assign tags (deploy)", "skipped", "no tags selected");
        toast.info("Published without tags — no deployment was triggered");
      } else if (!publish) {
        recordStep("Assign tags (deploy)", "skipped", "draft mode");
      }

      // After app creation, save PPPC policy if user configured one (or if an
      // existing policy is bound to this bundle and tags need reconciling),
      // then deploy it by binding the same tags as the app — without this
      // step the policy is created but never reaches any device.
      //
      // We also enter this block when the wizard has no PPPC choices but a
      // policy already exists for the bundle: publishing the app should still
      // bind/unbind that existing policy to the selected deployment tags so
      // the policy-tag API call actually fires on assignment.
      const shouldRunPppcReconcile =
        isMacOs &&
        bundleId &&
        (hasPppcChoices ||
          (!!existingPppcPolicyId &&
            (publish || existingPppcTagIds.length > 0)));

      if (shouldRunPppcReconcile) {
        // Deploy gate (matches app-tag gate above): in publish mode with
        // tags selected we hand the hook the next tag set so it reconciles
        // and binds the policy to those tags. In draft / no-tags we save
        // the policy with no tags, so it exists but isn't deployed yet.
        const willDeploy = publish && selectedTags.length > 0;
        const nextTagIds = willDeploy ? selectedTags : [];
        try {
          // If the wizard has no choices but an existing policy is being
          // re-tagged, preserve the existing choices/codeRequirement so the
          // update doesn't wipe the policy contents.
          const choicesForSave = hasPppcChoices
            ? pppcValue.choices
            : (existingPppcPolicy?.choices ?? {});
          const codeReqForSave = hasPppcChoices
            ? pppcValue.codeRequirement
            : (existingPppcPolicy?.codeRequirement ?? "");
          const saved = await savePppcMutation.mutateAsync({
            existingPolicyId: existingPppcPolicyId,
            appName,
            bundleId,
            codeRequirement: codeReqForSave,
            choices: choicesForSave,
            tagIds: nextTagIds,
            previousTagIds: existingPppcTagIds,
          });
          recordStep("Save PPPC policy", "success");
          if (willDeploy) {
            recordStep("Assign PPPC tags (deploy)", "success");
            // Audit: record publish-time deployment intent so it appears on
            // the Application detail timeline alongside the device-side
            // "installed" confirmations the agent reports back later.
            recordPppcActivity({
              policyId: saved.id,
              action: "deployed",
              actor: getCurrentUserEmail(),
              bundleId,
              tagIds: nextTagIds,
              previousTagIds: existingPppcTagIds,
              summary: `${appName || "PPPC policy"} published to ${nextTagIds.length} tag${nextTagIds.length === 1 ? "" : "s"}`,
              details: {
                tagCount: nextTagIds.length,
                viaWizard: "AddApplicationPage",
                reusedExistingPolicy: !!existingPppcPolicyId,
              },
            });
          } else if (publish && selectedTags.length === 0) {
            recordStep("Assign PPPC tags (deploy)", "skipped", "no tags selected");
          } else {
            recordStep("Assign PPPC tags (deploy)", "skipped", "draft mode");
          }
        } catch (e) {
          // useSavePppcPolicy already toasts the error; we just record
          // telemetry. The save+reconcile run as a single mutation, so we
          // can't distinguish which sub-step failed from out here — log
          // both as error so the failure is visible in the attempt trace.
          console.error("PPPC save/deploy failed:", e);
          recordStep("Save PPPC policy", "error", errorCodeOf(e));
          if (willDeploy) {
            recordStep("Assign PPPC tags (deploy)", "error", errorCodeOf(e));
          }
        }
      } else {
        recordStep("Save PPPC policy", "skipped", isMacOs ? "no choices" : "non-macOS");
      }

      finishAttempt("success");
      navigate('/applications');
    } catch (error) {
      console.error("Failed to add application:", error);
      const message = error instanceof Error ? error.message : "Failed to add application";
      recordStep("Submit failed", "error", errorCodeOf(error));
      finishAttempt("error");
      toast.error(message);
    } finally {
      // Always re-enable the buttons, even on error/early-return paths above.
      setSubmitMode(null);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "select":
        return <SelectAppStep onSelectApp={handleSelectApp} selectedAppId={getSelectedAppId()} initialSearchTerm={prefillSearch} />;
      case "assignment":
        return selectedApp ? (
          <AppAssignmentStep
            selectedApp={selectedApp}
            customers={selectedCustomers}
            groups={selectedGroups}
            tags={selectedTags}
            setCustomers={setSelectedCustomers}
            setGroups={setSelectedGroups}
            setTags={setSelectedTags}
            isAbmApp={isAbmApp}
            preSelectedCustomer={abmCustomer}
          />
        ) : null;
      case "configuration":
        return (
          <AppConfigurationStep
            configuration={initialConfiguration}
            onConfigurationChange={setInitialConfiguration}
          />
        );
      case "pppc":
        return (
          <PppcStep
            bundleId={bundleId}
            appName={appName}
            value={pppcValue}
            onChange={(v) => {
              setPppcValue(v);
              setPppcErrors({});
            }}
            errors={pppcErrors}
            disabled={!isMacOs}
            disabledReason={
              !isMacOs
                ? "Privacy Permissions only apply to macOS apps. Your draft is preserved — change the platform back to macOS to continue editing, or skip this step."
                : undefined
            }
          />
        );
      default:
        return null;
    }
  };

  const renderPrimaryAction = () => {
    if (currentStep === "select") {
      return <Button onClick={handleNext} disabled={!canProceedFromStep("select")}>Continue</Button>;
    }
    if (currentStep === "assignment") {
      return <Button onClick={handleNext} disabled={!canProceedFromStep("assignment")}>Continue</Button>;
    }
    // configuration / pppc — both can be skipped or saved
    if (!isLastStep) {
      return (
        <>
          <Button variant="outline" onClick={() => {
            if (currentStep === "configuration") setInitialConfiguration(null);
            if (currentStep === "pppc") handleSkipPppc();
            handleNext();
          }}>
            Skip
          </Button>
          <Button onClick={handleNext}>
            {currentStep === "pppc" ? "Save & Continue" : "Continue"}
          </Button>
        </>
      );
    }
    // Last step — show Add Application dropdown
    return (
      <>
        {currentStep === "pppc" && (
          <Button variant="outline" onClick={handleSkipPppc} disabled={isSubmitting}>
            Skip PPPC
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button disabled={isSubmitting} aria-busy={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {submitMode === "publish" ? "Publishing…" : "Saving draft…"}
                </>
              ) : (
                <>
                  Add Application
                  <ChevronDown className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem
              disabled={isSubmitting}
              onSelect={(e) => { e.preventDefault(); void handleAddApplication(false); }}
            >
              {submitMode === "draft" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save as Draft
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={isSubmitting}
              onSelect={(e) => { e.preventDefault(); void handleAddApplication(true); }}
            >
              {submitMode === "publish" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Add and Publish
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    );
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className={cx("flex items-center justify-between px-6 py-4 border-b bg-background", "flex items-center justify-between px-6 py-2 border-b bg-background")}>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
          <h1 className={cx("text-xl font-semibold", "text-base font-semibold")}>Add Application</h1>
        </div>
        <div className="flex items-center gap-2">
          {attempt && !showTimeline && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowTimeline(true)}
              aria-label="Show submission timeline"
            >
              <Activity className="h-4 w-4 mr-1" />
              Timeline
            </Button>
          )}
          {currentStep !== "select" && <Button variant="outline" onClick={handleBack}>Previous</Button>}
          {renderPrimaryAction()}
        </div>
      </div>
      <ApplicationWizardSteps currentStep={currentStep} completedSteps={completedSteps} showPppcStep={isMacOs} />
      {remediationContext && currentStep === "select" && (
        <div className="mx-6 mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <div className="font-medium text-destructive">
            Remediating {remediationContext.appName}
          </div>
          <div className="text-muted-foreground mt-1">
            {remediationContext.cveCount} CVE{remediationContext.cveCount === 1 ? "" : "s"} matched
            {remediationContext.vulnerableVersions.length > 0 && (
              <> · vulnerable version{remediationContext.vulnerableVersions.length === 1 ? "" : "s"}: <span className="font-mono">{remediationContext.vulnerableVersions.join(", ")}</span></>
            )}
            . Select the latest store build below, then assign to your fleet to push the update.
          </div>
        </div>
      )}
      <div className="flex-1 overflow-auto">
        {/* data-error-anchor lets focusFirstError() locate the active step's
            content root without each step component needing to know about it. */}
        <div className={cx("p-6", "p-3")} data-error-anchor={currentStep}>
          {renderStepContent()}
        </div>
      </div>

      {/* Submission timeline: lightweight per-attempt log of step outcomes.
          Stays on screen after success/error so the user can review what ran. */}
      {attempt && showTimeline && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 right-4 z-40 w-80 max-w-[calc(100vw-2rem)] rounded-lg border bg-card text-card-foreground shadow-lg"
        >
          <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              {attempt.outcome === "running" && <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />}
              {attempt.outcome === "success" && <CheckCircle2 className="h-4 w-4 text-success shrink-0" />}
              {attempt.outcome === "error" && <XCircle className="h-4 w-4 text-destructive shrink-0" />}
              <span className="text-sm font-medium truncate">
                {attempt.mode === "publish" ? "Add & Publish" : "Save as Draft"}
              </span>
              <span className="text-xs text-muted-foreground shrink-0">
                {new Date(attempt.startedAt).toLocaleTimeString()}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowTimeline(false)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Hide timeline"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <ol className="max-h-64 overflow-auto px-3 py-2 space-y-1.5">
            {attempt.events.length === 0 && (
              <li className="text-xs text-muted-foreground italic">Waiting for first step…</li>
            )}
            {attempt.events.map((evt, i) => (
              <li key={i} className="flex items-start gap-2 text-xs">
                {evt.status === "success" && <CheckCircle2 className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" />}
                {evt.status === "error" && <XCircle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />}
                {evt.status === "skipped" && <MinusCircle className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />}
                {evt.status === "running" && <Loader2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0 animate-spin" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium truncate">{evt.label}</span>
                    <span className="text-muted-foreground tabular-nums shrink-0">+{(evt.at / 1000).toFixed(2)}s</span>
                  </div>
                  {evt.errorCode && (
                    <div className={evt.status === "error" ? "text-destructive truncate" : "text-muted-foreground truncate"}>
                      {evt.errorCode}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
          {attempt.finishedAt !== null && (
            <div className="border-t px-3 py-1.5 text-xs text-muted-foreground tabular-nums">
              Total: {((attempt.finishedAt - attempt.startedAt) / 1000).toFixed(2)}s · #{attempt.id}
            </div>
          )}
        </div>
      )}

      {/* Confirmation dialog shown when the user tries to leave the wizard
          while a Save-as-Draft or Add & Publish flow is still running. */}
      <AlertDialog
        open={pendingNav !== null}
        onOpenChange={(open) => { if (!open) setPendingNav(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave while {submitMode === "publish" ? "publishing" : "saving"}?</AlertDialogTitle>
            <AlertDialogDescription>
              {submitMode === "publish"
                ? "Publishing is still in progress. Leaving now may interrupt deployment and tag assignment."
                : "Saving the draft is still in progress. Leaving now may lose unsaved data."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay on page</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const action = pendingNav;
                setPendingNav(null);
                if (action) action();
              }}
            >
              Leave anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
